import React, { forwardRef, useMemo, useCallback, useState, useRef } from 'react';
import _ from 'lodash';
import { FieldTypes, urlRegexValidator } from 'utils/formUtils';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import GenericPullCredentialsForm from '../GenericPullCredentialsForm';
import FiniteStateMappingForm from './FiniteStateMappingForm';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';

function FiniteStateCredentialsForm(props, ref) {
    const featureFlags = useFeatureFlag();
    const [token, setToken] = useState();
    const organisationsRef = useRef([]);
    const { orgId, orgName, client_id, organisation_context, graph_ql = "false", nextGenAPI = "false"} = props.config || {};
    const [apiType, setApiType] = useState(nextGenAPI === "true" ? "nextGenAPI" : graph_ql === "false" ? false : true);
    const [clientId, setClientId] = useState(client_id);
    const [clientSecret, setClientSecret] = useState();
    const [organisationContext, setOrganisationContext] = useState(organisation_context);

    const handleApiTypeChange = useCallback((e) => {
        const { value } = e.target;

        let selectedAPIValue;
        if (value === "nextGenAPI") {
            selectedAPIValue = "nextGenAPI";
        } else if (value === "false") {
            selectedAPIValue = false;
        } else {
            selectedAPIValue = true;
        }

        setApiType(selectedAPIValue);
    }, []);


    const organisationConfig = useMemo(() => {
        if (clientId && clientSecret && organisationContext) {
            return { client_id: clientId, client_secret: clientSecret, organisation_context: organisationContext, graph_ql: true }
        } else {
            return {}
        }
    }, [clientId, clientSecret, organisationContext])

    const fetchOrganisations = useCallback((token) => {
        if (!apiType && token) {
            const { promise, cancel } = ToolsIntegrationsAPI.getFiniteStateOrganisations(token);
            const modifiedPromise = promise.then((response) => {
                organisationsRef.current = response.projects;
                return response.projects;
            });
            return { promise: modifiedPromise, cancel };
        }

        if (apiType && !_.isEmpty(organisationConfig)) {
            const { promise, cancel } = ToolsIntegrationsAPI.getFiniteStateOrganisationsWithConfig(organisationConfig);
            const modifiedPromise = promise.then((response) => {
                organisationsRef.current = response.projects;
                return response.projects;
            });
            return { promise: modifiedPromise, cancel };
        }

        if (!token || _.isEmpty(organisationConfig)) {
            let response = [];
            if (orgId && orgName) {
                response = [{ name: orgName, id: orgId }];
            }
            organisationsRef.current = response;
            return { promise: Promise.resolve(response), cancel: _.noop };
        }
    }, [orgId, orgName, apiType, organisationConfig]);

    const parseExtraFields = useCallback((config) => {
      const { graph_ql, nextGenAPI } = config
        return {
            ...config,
            graph_ql: nextGenAPI === "true" ? "nextGenAPI" : graph_ql
        }
    }, [graph_ql])

   const fields = useMemo(() => [
  {
    name: "graph_ql",
    type: FieldTypes.RADIO,
    options: [
      { label: "Legacy API", value: "false" },
      { label: "GraphQL API", value: "true" },
      ...(featureFlags.FINITE_STATE_NEXT_GEN_API
        ? [{ label: "REST API", value: "nextGenAPI" }]
        : []),
    ],
    props: {
      onChange: handleApiTypeChange,
      defaultValue: apiType.toString(),
    },
  },

  // Conditional fields for API type
  ...(apiType === "nextGenAPI"
    ? [
        {
          name: "apiEndpoint",
          label: "Host URL",
          required: true,
          rules: [urlRegexValidator],
        },
        {
          name: "token",
          label: "Token",
          required: true,
          type: FieldTypes.PASSWORD,
          props: {
            onChange: (e) => setToken(e.target.value),
          },
        },
      ]
    : apiType
    ? [
        {
          name: "client_id",
          label: "Client Id",
          required: true,
          props: {
            onChange: (e) => setClientId(e.target.value),
          },
        },
        {
          name: "client_secret",
          label: "Client Secret",
          type: FieldTypes.PASSWORD,
          required: true,
          props: {
            onChange: (e) => setClientSecret(e.target.value),
          },
        },
        {
          name: "organisation_context",
          label: "Organisation Context",
          required: true,
          props: {
            onChange: (e) => setOrganisationContext(e.target.value),
          },
        },
      ]
    : [
        {
          name: "token",
          label: "Token",
          type: FieldTypes.PASSWORD,
          required: true,
          props: {
            onChange: (e) => setToken(e.target.value),
          },
        },
      ]),

  // Common field (only when NOT nextGenAPI)
  ...(apiType !== "nextGenAPI"
    ? [
        {
          name: "orgId",
          type: FieldTypes.SELECT,
          label: "Portfolio",
          required: true,
          props: {
            api: fetchOrganisations,
            apiParams: token,
          },
        },
      ]
    : []),
], [fetchOrganisations, token, handleApiTypeChange, apiType, featureFlags.FINITE_STATE_NEXT_GEN_API]);


    const beforeSave = useCallback((data) => {
        if(data.graph_ql === "nextGenAPI") {
            data.graph_ql = false;
            data.nextGenAPI = true;
        } else {
            data.orgName = _.find(organisationsRef.current, (org) => org.id === data.orgId)?.name;
        }
        return data;
    }, []);

    return (
        <GenericPullCredentialsForm
            {...props}
            ref={ref}
            fields={fields}
            beforeSave={beforeSave}
            MappingForm={FiniteStateMappingForm}
            parseExtraFields={parseExtraFields}
        />
    )
}

export default forwardRef(FiniteStateCredentialsForm);